import { Component, Prop, h } from '@stencil/core';
import type { AwardsLayoutPreset } from '../../../../awards/type-1/index';

const BASE = 'awards-type-1';

/**
 * Awards component for SignNow brand showcasing awards and badges
 *
 * @product signnow
 * @version 1.2.0
 * @category SignNow
 * @contentConfig Awards component for displaying company awards and badges
 * @slot heading - {required}, <me-heading variant="2" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Main section heading
 * @slot logo - {required}, {multiple}, <me-logo src="..." href="..." alt="..." aria-label="..."> - Award badge item
 * @example
 *   <sn-awards-type-1 layout="splitReverse">
 *     <me-heading slot="heading" variant="2" tag="h4">Award-winning eSignature solution</me-heading>
 *     <me-logo slot="logo" src="https://marketing-static.signnow-dev.xyz/1724/images/global/badges/leader-summer-2026.svg" href="https://www.g2.com/products/signnow/reviews?utm_source=rewards-badge" alt="G2 Crowd Award" aria-label="airSlate SignNow is a leader in Mid-Market E-Signature on G2" lazy="false"></me-logo>
 *     <me-logo slot="logo" src="https://marketing-static.signnow.com/350/images/global/badges/High-Performer-Enterprise-Winter-2025.svg" href="https://www.g2.com/products/signnow/reviews?utm_source=rewards-badge" alt="G2 Crowd Award" aria-label="airSlate SignNow is a leader in Enterprise E-Signature on G2" lazy="false"></me-logo>
 *     <me-logo slot="logo" src="https://marketing-static.signnow.com/350/images/global/badges/Leader-Winter-2025.svg" href="https://www.g2.com/products/signnow/reviews?utm_source=rewards-badge" alt="G2 Crowd Award" aria-label="airSlate SignNow is a leader in E-Signature on G2" lazy="false"></me-logo>
 *     <me-logo slot="logo" src="https://marketing-static.signnow.com/350/images/global/badges/Small-Business-Winter-2025.svg" href="https://www.g2.com/products/signnow/reviews?utm_source=rewards-badge" alt="G2 Crowd Award" aria-label="airSlate SignNow is a leader in Small-Business E-Signature on G2" lazy="false"></me-logo>
 *   </sn-awards-type-1>
 */
@Component({
  tag: 'sn-awards-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNAwardsType1 {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Passed through to `awards-type-1` (`split` | `center` | `splitReverse` | `spread`).
   * @default split
   */
  @Prop() layout: AwardsLayoutPreset = 'split';

  /**
   * Use smaller text size for long headings
   */
  @Prop() longText: boolean = false;

  private getRowProps(): {
    directionLg: 'row' | 'row-reverse';
    justify: 'start' | 'center' | 'between';
    justifyLg: 'start' | 'center' | 'between' | 'end';
    align: 'start' | 'center' | 'stretch';
    alignLg: 'start' | 'center' | 'stretch';
  } {
    switch (this.layout) {
      case 'center':
        return { directionLg: 'row', justify: 'start', justifyLg: 'center', align: 'center', alignLg: 'center' };
      case 'splitReverse':
        return { directionLg: 'row-reverse', justify: 'start', justifyLg: 'end', align: 'stretch', alignLg: 'center' };
      case 'spread':
        return { directionLg: 'row', justify: 'start', justifyLg: 'between', align: 'stretch', alignLg: 'center' };
      case 'split':
      default:
        return { directionLg: 'row', justify: 'start', justifyLg: 'start', align: 'stretch', alignLg: 'center' };
    }
  }

  render() {
    const row = this.getRowProps();
    const sectionClasses = [BASE, `${BASE}--layout-${this.layout}`, this.longText ? `${BASE}--long-text` : ''].filter(Boolean).join(' ');

    return (
      <me-section class={sectionClasses} yGap="3xl" yGapLg="4xl" role="region" aria-label="Awards and Recognition">
        <me-row
          align={row.align}
          alignLg={row.alignLg}
          direction="column"
          yGap="2xl"
          yGapLg="none"
          xGap="none"
          xGapLg="5xl"
          justify={row.justify}
          justifyLg={row.justifyLg}
          directionLg={row.directionLg}
          class={`${BASE}__container`}
        >
          <me-column span="12" spanMd="6" spanLg="5" class={`${BASE}__text`}>
            <slot name="heading"></slot>
          </me-column>
          <me-column span="12" spanMd="6" spanLg="7" class={`${BASE}__logos`}>
            <div class={`${BASE}__logos-list`}>
              <slot name="logo"></slot>
            </div>
          </me-column>
        </me-row>
      </me-section>
    );
  }
}
